import React from 'react';
import {
  Alert, Col, Container, Row,
} from 'react-bootstrap';
import api from 'api';
import useFeedback from 'hooks/useFeedback';
import useRequest from 'hooks/useRequest';
import SectionWrapper from 'components/layout/SectionWrapper';
import Loader from 'components/elements/Loader';
import MessagePageHeader from 'components/messages/MessagePageHeader';

import PostMessageForm from './PostMessageForm';
import PostedMessagesList from './PostedMessagesList';
import {
  AnnouncementListResponse,
  AnnouncementResponse,
  CreateMessagePayload,
  parseAnnouncementList,
  PostedMessage,
  toPostedMessage,
} from './types';

const PostMessagesPage: React.FC = () => {
  const { setError, setSuccess } = useFeedback();
  const [postedMessages, setPostedMessages] = React.useState<PostedMessage[]>([]);
  const [historyMonths, setHistoryMonths] = React.useState<number | undefined>();
  const [editingMessage, setEditingMessage] = React.useState<PostedMessage | null>(null);
  const formRef = React.useRef<HTMLDivElement>(null);

  const {
    doRequest: fetchAnnouncements,
    isLoading,
    error,
  } = useRequest<AnnouncementListResponse>(api.getAnnouncements);

  const {
    doRequest: createAnnouncement,
    isLoading: isPosting,
  } = useRequest<AnnouncementResponse, CreateMessagePayload>(api.createAnnouncement);

  const {
    doRequest: updateAnnouncement,
    isLoading: isUpdating,
  } = useRequest<AnnouncementResponse, CreateMessagePayload>(api.updateAnnouncement);

  const loadAnnouncements = React.useCallback(async () => {
    const data = await fetchAnnouncements(null);
    const { historyMonths: months, items } = parseAnnouncementList(data);
    setHistoryMonths(months);
    setPostedMessages(items.map(toPostedMessage));
  }, [fetchAnnouncements]);

  React.useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handleSubmit = async (payload: CreateMessagePayload) => {
    if (editingMessage) {
      const updated = await updateAnnouncement(payload, editingMessage.id);
      if (!updated) {
        setError('Failed to update message.');
        return;
      }

      setPostedMessages((prev) => prev.map(
        (message) => (message.id === editingMessage.id ? toPostedMessage(updated) : message),
      ));
      setEditingMessage(null);
      setSuccess('Message updated successfully.');
      return;
    }

    const created = await createAnnouncement(payload);
    if (!created) {
      setError('Failed to post message.');
      return;
    }

    setPostedMessages((prev) => [toPostedMessage(created), ...prev]);
    setSuccess('Message posted successfully.');
  };

  const handleEdit = (message: PostedMessage) => {
    setEditingMessage(message);
    window.requestAnimationFrame(() => {
      const scrollContainer = document.getElementById('app-main-scroll');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleDelete = async (id: string) => {
    if (editingMessage?.id === id) {
      setEditingMessage(null);
    }

    try {
      await api.deleteAnnouncement(id);
      setPostedMessages((prev) => prev.filter((message) => message.id !== id));
      setSuccess('Message deleted successfully.');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.title;
      setError(detail || 'Failed to delete message.');
    }
  };

  return (
    <Container className="my-5 site-content">
      <SectionWrapper>
        <MessagePageHeader
          title="Post a Message"
          description="Send announcements to users. The title appears in the header mailbox; the full content shows on the Updates page."
        />

        {error && (
          <Alert variant="danger" className="mb-4">
            Failed to load posted messages.
          </Alert>
        )}

        <Row className="g-4">
          <Col lg={6}>
            <div ref={formRef} style={{ scrollMarginTop: '1rem' }}>
              <PostMessageForm
                onSubmit={handleSubmit}
                isSubmitting={isPosting || isUpdating}
                editingMessage={editingMessage}
                onCancelEdit={() => setEditingMessage(null)}
              />
            </div>
          </Col>
          <Col lg={6} style={{ minWidth: 0 }}>
            {isLoading ? (
              <Loader />
            ) : (
              <PostedMessagesList
                messages={postedMessages}
                historyMonths={historyMonths}
                editingMessageId={editingMessage?.id}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            )}
          </Col>
        </Row>
      </SectionWrapper>
    </Container>
  );
};

export default PostMessagesPage;
